/**
 * ChatMessageWidget — Rich content renderer for AI-generated widgets
 *
 * When the AI sends structured JSON (e.g. approval form, table, chart data),
 * this component renders it as an interactive UI element.
 *
 * Supported widget types:
 *   approval — Title + approve/reject buttons (sends action back)
 *   table    — Simple data table
 *   form     — Key/value input form
 *   list     — Styled bullet list
 *   info     — Highlighted info card
 *   (fallback: plain markdown text)
 */

import type { ChatWidget } from '@/hooks/useChatStream';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, XCircle, Info, List, Table2 } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Props {
  widget: ChatWidget;
  /** Called when user interacts with an action widget */
  onAction?: (actionType: string, payload: unknown) => Promise<unknown> | void;
}

/** Approval widget — human-in-the-loop decision */
function ApprovalWidget({ widget, onAction }: { widget: ChatWidget; onAction?: Props['onAction'] }) {
  const options = (widget.options as Array<{ label: string; value: string; variant?: string }> | undefined) ?? [
    { label: 'Approve', value: 'approved', variant: 'default' },
    { label: 'Reject', value: 'rejected', variant: 'destructive' },
  ];
  return (
    <div className="rounded-lg border bg-amber-50/60 dark:bg-amber-950/20 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
        <div>
          <p className="font-medium text-sm">{String(widget.title ?? 'Approval Required')}</p>
          {widget.description != null && <p className="text-xs text-muted-foreground mt-0.5">{String(widget.description)}</p>}
        </div>
      </div>
      {widget.details != null && (
        <div className="text-xs bg-background rounded p-2 border space-y-1">
          {Object.entries(widget.details as Record<string, unknown>).map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="font-medium w-28 shrink-0 text-muted-foreground capitalize">{k.replace(/_/g, ' ')}</span>
              <span>{String(v)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        {options.map(opt => (
          <Button
            key={opt.value}
            size="sm"
            variant={opt.variant === 'destructive' ? 'destructive' : opt.variant === 'outline' ? 'outline' : 'default'}
            onClick={() => { void onAction?.('approval', { decision: opt.value, widgetId: widget.id }); }}
          >
            {opt.variant === 'destructive' ? <XCircle className="mr-1.5 h-3.5 w-3.5" /> : <CheckCircle className="mr-1.5 h-3.5 w-3.5" />}
            {opt.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

/** Table widget */
function TableWidget({ widget }: { widget: ChatWidget }) {
  const rows = (widget.rows as unknown[][] | undefined) ?? [];
  const headers = (widget.headers as string[] | undefined) ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="rounded-lg border overflow-x-auto">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        <Table2 className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{String(widget.title ?? 'Data')}</span>
      </div>
      <table className="w-full text-xs">
        {headers.length > 0 && (
          <thead>
            <tr className="border-b bg-muted/20">
              {headers.map(h => <th key={h} className="px-3 py-1.5 text-left font-medium text-muted-foreground">{h}</th>)}
            </tr>
          </thead>
        )}
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b last:border-0 hover:bg-muted/10">
              {(Array.isArray(row) ? row : [row]).map((cell, j) => (
                <td key={j} className="px-3 py-1.5">{String(cell ?? '')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Form widget — AI asks user to fill fields */
function FormWidget({ widget, onAction }: { widget: ChatWidget; onAction?: Props['onAction'] }) {
  const fields = useMemo(
    () => (widget.fields as Array<{
      name: string;
      label: string;
      type?: string;
      required?: boolean;
      placeholder?: string;
      defaultValue?: unknown;
      options?: Array<{ label: string; value: string }>;
    }> | undefined) ?? [],
    [widget.fields],
  );
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const initial = { ...((widget.values as Record<string, unknown> | undefined) ?? {}) };
    for (const field of fields) {
      if (initial[field.name] === undefined && field.defaultValue !== undefined) {
        initial[field.name] = field.defaultValue;
      }
    }
    return initial;
  });
  const isActionForm = widget.type === 'action_form';
  const submitLabel = String(widget.submitLabel ?? (isActionForm ? 'Preview' : 'Submit'));
  const actionId = typeof widget.actionId === 'string' ? widget.actionId : undefined;
  const executionMode = typeof widget.executionMode === 'string' ? widget.executionMode : 'dry-run';

  const updateValue = (name: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="rounded-lg border bg-background p-4 space-y-3">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 text-primary" />
        <p className="font-medium text-sm">{String(widget.title ?? 'Please fill in')}</p>
      </div>
      <div className="space-y-2">
        {fields.map(f => (
          <div key={f.name} className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">
              {f.label}{f.required ? ' *' : ''}
            </label>
            {f.type === 'select' ? (
              <select
                value={String(values[f.name] ?? '')}
                className="rounded border px-2 py-1 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                onChange={e => { updateValue(f.name, e.target.value); }}
              >
                <option value="">Select...</option>
                {(f.options ?? []).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : (
              <input
                type={f.type === 'password' || f.type === 'email' ? f.type : 'text'}
                value={String(values[f.name] ?? '')}
                placeholder={f.placeholder}
                className="rounded border px-2 py-1 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                onChange={e => { updateValue(f.name, e.target.value); }}
              />
            )}
          </div>
        ))}
      </div>
      <Button
        size="sm"
        onClick={() => {
          void onAction?.(isActionForm ? 'action_form_submit' : 'form_submit', {
            values,
            widgetId: widget.id,
            actionId,
            executionMode,
            schemaVersion: widget.schemaVersion,
          });
        }}
      >
        {submitLabel}
      </Button>
    </div>
  );
}

/** List widget */
function ListWidget({ widget }: { widget: ChatWidget }) {
  const items = (widget.items as string[] | undefined) ?? [];
  return (
    <div className="rounded-lg border p-3 space-y-1.5">
      <div className="flex items-center gap-2 mb-2">
        <List className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium">{String(widget.title ?? '')}</span>
      </div>
      {items.map((item, i) => (
        <div key={i} className="flex items-start gap-2 text-sm">
          <Badge variant="outline" className="shrink-0 text-[10px] h-4 px-1">{i + 1}</Badge>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );
}

/** Info card widget */
function InfoWidget({ widget }: { widget: ChatWidget }) {
  return (
    <div className="rounded-lg border border-primary/20 bg-primary/5 dark:border-primary/30 dark:bg-primary/5 p-3">
      <div className="flex items-start gap-2">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <div>
          {widget.title && <p className="font-medium text-sm mb-1">{String(widget.title)}</p>}
          <p className="text-sm text-muted-foreground">{String(widget.content ?? widget.message ?? '')}</p>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────

export function ChatMessageWidget({ widget, onAction }: Props) {
  switch (widget.type) {
    case 'approval': return <ApprovalWidget widget={widget} onAction={onAction} />;
    case 'table':    return <TableWidget widget={widget} />;
    case 'form':
    case 'action_form': return <FormWidget widget={widget} onAction={onAction} />;
    case 'list':     return <ListWidget widget={widget} />;
    case 'info':     return <InfoWidget widget={widget} />;
    default:         return <InfoWidget widget={widget} />;
  }
}
